import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCookieConsent, CookieConsent } from '../contexts/CookieContext';
import { Link } from 'react-router-dom';

const CookieBanner: React.FC = () => {
  const { showBanner, updateConsent, consent, setShowBanner } = useCookieConsent();
  const [showSettings, setShowSettings] = useState(false);
  const [localConsent, setLocalConsent] = useState<CookieConsent>(
    consent || { essential: true, analytics: false, externalMedia: false }
  );

  const dialogRef = useRef<HTMLDivElement>(null);

  const handleAcceptAll = useCallback(() => {
    updateConsent({ essential: true, analytics: true, externalMedia: true });
  }, [updateConsent]);

  const handleAcceptEssential = useCallback(() => {
    updateConsent({ essential: true, analytics: false, externalMedia: false });
  }, [updateConsent]);

  const handleSaveSettings = useCallback(() => {
    updateConsent(localConsent);
    setShowSettings(false);
  }, [updateConsent, localConsent]);

  const toggleAnalytics = () => {
    setLocalConsent(prev => ({ ...prev, analytics: !prev.analytics }));
  };

  const toggleExternalMedia = () => {
    setLocalConsent(prev => ({ ...prev, externalMedia: !prev.externalMedia }));
  };

  useEffect(() => {
    if (!showBanner) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleAcceptEssential();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const timer = setTimeout(() => {
      const firstBtn = dialogRef.current?.querySelector<HTMLElement>('button');
      firstBtn?.focus();
    }, 100);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      clearTimeout(timer);
    };
  }, [showBanner, handleAcceptEssential]);

  if (!showBanner) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center p-4 bg-black/50 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Cookie-Einstellungen"
      ref={dialogRef}
    >
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
        <div className="p-6 sm:p-8">
          {!showSettings ? (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Cookie-Einstellungen</h2>
                <p className="text-gray-600 leading-relaxed">
                  Wir schätzen Ihre Privatsphäre. Wir verwenden Cookies, um Ihr Erlebnis zu verbessern, den Traffic zu analysieren und externe Inhalte anzuzeigen.
                  Bitte wählen Sie aus, welche Cookies Sie zulassen möchten. Weitere Informationen finden Sie in unserer{' '}
                  <Link to="/datenschutz" className="text-blue-600 hover:underline" onClick={() => setShowBanner(false)}>
                    Datenschutzerklärung
                  </Link>.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  onClick={() => setShowSettings(true)}
                  className="order-3 sm:order-1 px-6 py-2.5 text-gray-700 hover:bg-gray-100 font-medium rounded-lg transition-colors"
                >
                  Anpassen
                </button>
                <button
                  onClick={handleAcceptEssential}
                  className="order-2 sm:order-2 px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-colors"
                >
                  Nur notwendige
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="order-1 sm:order-3 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition-colors"
                >
                  Alle akzeptieren
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Einstellungen anpassen</h2>
                <p className="text-sm text-gray-500 mb-6">
                  Wählen Sie aus, welche Kategorien von Cookies Sie zulassen möchten.
                </p>

                <div className="space-y-4">
                  {/* Essential */}
                  <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="pr-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">Notwendig</span>
                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-gray-200 text-gray-600 rounded">Immer aktiv</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Diese Cookies sind für den technischen Betrieb der Website erforderlich.
                      </p>
                    </div>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="pr-4">
                      <span className="block font-semibold text-gray-900 mb-1">Statistiken</span>
                      <p className="text-sm text-gray-600">
                        Wir nutzen Google Analytics, um zu verstehen, wie Besucher mit unserer Website interagieren.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer mt-1">
                      <input
                        type="checkbox"
                        checked={localConsent.analytics}
                        onChange={toggleAnalytics}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  {/* External Media */}
                  <div className="flex items-start justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="pr-4">
                      <span className="block font-semibold text-gray-900 mb-1">Externe Medien</span>
                      <p className="text-sm text-gray-600">
                        Wetter-Widgets (Meteoblue, METAR) und Petitionen (openPetition). Wenn deaktiviert, müssen diese einzeln aktiviert werden.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer mt-1">
                      <input
                        type="checkbox"
                        checked={localConsent.externalMedia}
                        onChange={toggleExternalMedia}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-6 py-2.5 text-gray-700 hover:bg-gray-100 font-medium rounded-lg transition-colors"
                >
                  Zurück
                </button>
                <button
                  onClick={handleSaveSettings}
                  className="px-8 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition-colors"
                >
                  Auswahl speichern
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
